import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_VERIZON_REVEAL_APP, EnableVerizonRevealAppInput } from '../data/mutations'

/**
 * Saves VerizonReveal app's username and password.
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const VerizonRevealModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableVerizonRevealApp, { loading }] = useMutation<
    { enableVerizonRevealApp: { appEnabled: boolean } },
    EnableVerizonRevealAppInput
  >(ENABLE_VERIZON_REVEAL_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Verizon Reveal app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Your username or password is invalid.') {
        setFormError('Invalid username/password. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid username/password. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { appPassword: string; appUsername: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableVerizonRevealApp({
      variables: {
        appType: appType,
        password: event.appPassword,
        username: event.appUsername
      }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/verizon-connect"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable Verizon-Reveal for Chaine`}
        subtitle={`Enabling this integration requires an admin Verizon-Reveal account. Enter your username and password.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'Verizon-Reveal username',
            name: 'appUsername',

            placeHolder: 'Enter your Verizon-Reveal username',
            type: 'input'
          },
          {
            labelText: 'Verizon-Reveal password',
            name: 'appPassword',

            placeHolder: 'Enter your Verizon-Reveal password',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

VerizonRevealModal.displayName = 'VerizonRevealModal'

export { VerizonRevealModal }
